import React, { useEffect } from "react";
import { inject, observer } from "mobx-react";
import {
  fn_setPrintOutType,
  fn_setOriginInfo,
  fn_setTestPrintOut,
  fn_printOutInit,
  fn_setTestPrintOutChange,
} from "function/FnBodySetting";
import { SectionTitle } from "components/ui/SectionTitle";
import SegmentedControl from "components/ui/SegmentedControl";
import { Switcher } from "components/ui/Switcher";
import { Radio, RadioGroup } from "components/ui/Radio";
import { Button } from "components/ui/Button";
import { PRINT_RADIO_CONFIGS, KITCHEN_PRINT_RADIO_CONFIGS } from "views/setting/constants/printRadioConfig";
import {
  RECEIPT_HALL_SECTIONS,
  RECEIPT_KITCHEN_SECTIONS,
  ReceiptItemConfig,
} from "views/setting/constants/printReceiptConfig";
import refreshIcon from "static-file/images/refresh_g.png";
import testPrintIcon from "static-file/images/icon_print.png";
import settingIcon from "static-file/images/settingIcon.png";

const PRINT_SIZE_OPTIONS = [
  { label: "작게", value: "1" },
  { label: "일반", value: "5" },
  { label: "크게", value: "6" },
];

// 출력물 양식 설정 레이아웃
const PrintoutLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="scroll tab">
      <div className="inner">
        <div className="wrap_print out">{children}</div>
      </div>
    </div>
  );
};

// 출력물 라디오 그룹 컴포넌트
const PrintRadioGroup = ({
  title,
  name,
  currentValue,
  onChange,
  disabled = false,
}: {
  title: string;
  name: string;
  currentValue: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) => {
  return (
    <li className="item flex items-center gap-4 text-xl font-">
      <span className={`tit min-w-[95px] ${disabled ? "text-text-secondary" : ""}`}>{title}</span>
      <div className="flex items-center justify-between w-full">
        <RadioGroup name={name} value={currentValue} onChange={e => onChange(e.target.value)} direction="row">
          {PRINT_SIZE_OPTIONS.map(option => (
            <Radio
              key={option.value}
              id={`${name}-${option.value}`}
              name={name}
              value={option.value}
              label={option.label}
              disabled={currentValue === "0"}
              className="!p-0 gap-1"
              size="sm"
            />
          ))}
        </RadioGroup>
      </div>
    </li>
  );
};

// 출력물 미리보기 컴포넌트
const ReceiptPrintOut = ({
  children,
  value,
  type,
}: {
  children: React.ReactNode;
  value: "1" | "5" | "6";
  type?: string;
}) => {
  let baseClass = "";
  if (value === "1") {
    baseClass = "default";
  } else if (value === "5") {
    baseClass = "strong";
  } else {
    baseClass = "strongMax";
  }

  const titleClass = type === "title" ? "title" : "";

  const className = `${baseClass} ${titleClass}`.trim();

  return <li className={className}>{children}</li>;
};

// 출력물 미리보기 아이템 렌더링
const ReceiptItemRenderer = ({ item, value }: { item: ReceiptItemConfig; value: any }) => {
  if (value[item.value || ""] === "0") {
    return null;
  }

  switch (item.type) {
    case "divider":
      return <li className={`line ${item.className || ""}`.trim()}></li>;

    case "label":
      return <li className="default">{item.content}</li>;

    case "printout":
      return (
        <ReceiptPrintOut value={value[item.value || ""]} type={item.className}>
          {item.content}
        </ReceiptPrintOut>
      );

    default:
      return null;
  }
};

// 포스주문번호 토글 컴포넌트
const PosNoToggle = ({ rootStore, checked, onChange }: { rootStore: any; checked: boolean; onChange: () => void }) => {
  // 출력물 새로고침 버튼 > 초기화
  const refreshPrintOut = (
    <Button variant="outline" fullWidth={false} className="!p-2" onClick={() => fn_printOutInit(rootStore)}>
      <img src={refreshIcon} alt="refresh" />
    </Button>
  );

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-end gap-2 border border-solid border-ui-border-subtle rounded-md px-3 py-2">
        <span className="text-sm font-bold text-text-secondary">포스주문번호 출력</span>
        <Switcher id="posNo" size="sm" switcherColor="primary" checked={checked} onChange={onChange} />
      </div>
      {refreshPrintOut}
    </div>
  );
};
const SettingOutPrinterOriginSetView = ({ rootStore }: { rootStore: any }) => {
  const { settingStore, layerStore, searchStore } = rootStore;
  const { getPrintOutReceipt0, getPrintOutReceipt1 } = settingStore;
  const printType = layerStore.getPrintOutType;

  useEffect(() => {
    settingStore.setSettingUseMenu("printer_setting");
    settingStore.setSettingView(false);
  }, [settingStore]);

  // 포스주문번호 출력 토글 핸들러
  const handleChangePosView = (type: "orderSlip" | "orderSheet") => {
    const ACTIVE_POS = "5";
    const INACTIVE_POS = "0";

    const config = {
      orderSlip: { receipt: getPrintOutReceipt0, location: "HALL" },
      orderSheet: { receipt: getPrintOutReceipt1, location: "KITCHEN" },
    };

    const { receipt, location } = config[type];
    const newPosNo = receipt.posNo === INACTIVE_POS ? ACTIVE_POS : INACTIVE_POS;

    fn_setTestPrintOutChange(rootStore, "HEADER", location, "posNo", newPosNo);
  };

  // 테스트 출력 버튼
  const testPrintOut = (
    <Button
      variant="default"
      fullWidth={false}
      className="!gap-1 !px-3 !py-2 !bg-gray-700"
      onClick={() => fn_setTestPrintOut(rootStore)}
    >
      <img src={testPrintIcon} alt="testPrint" />
      <span>테스트 출력</span>
    </Button>
  );

  // 메인 포스가 아닌 경우 조기 반환
  if (settingStore.getMainPosType === "N") {
    const noMainPosView = (
      <div className="box_tit origin">
        <h4>원산지 출력 설정</h4>
        <span className="tit">판매상품 원산지 등록 및 출력 설정</span>
        <div className="list_none subPos">
          <h5 className="tit">
            <span className="print">메인포스에서 설정해 주세요</span>
          </h5>
          <p className="print">현재 사용중인 포스는 {searchStore.getSession.connPosNo}번 포스입니다</p>
        </div>
      </div>
    );
    return <PrintoutLayout>{noMainPosView}</PrintoutLayout>;
  }

  return (
    <PrintoutLayout>
      <SectionTitle title="출력물 양식 설정" subTitle="원산지 등록 및 출력물 설정" addBottomBorder>
        <SegmentedControl
          options={[
            {
              value: "HALL",
              label: "주문전표",
              checked: printType === "HALL",
              onClick: () => fn_setPrintOutType(rootStore, "HALL"),
            },
            {
              value: "KITCHEN",
              label: "주문서",
              checked: printType === "KITCHEN",
              onClick: () => fn_setPrintOutType(rootStore, "KITCHEN"),
            },
          ]}
          size="md"
        />
      </SectionTitle>
      {printType === "HALL" ? (
        <div className="box">
          <div className="left">
            {/* 주문전표 영수증 미리보기 */}
            <ul className="preview">
              {RECEIPT_HALL_SECTIONS.map((section, index) => (
                <ReceiptItemRenderer key={`${section.type}-${index}`} item={section} value={getPrintOutReceipt0} />
              ))}
            </ul>
          </div>
          <div className="right">
            <PosNoToggle
              rootStore={rootStore}
              checked={getPrintOutReceipt0.posNo !== "0"}
              onChange={() => handleChangePosView("orderSlip")}
            />
            {/* 출력물 라디오 그룹 */}
            <ul className="list !mt-4">
              {PRINT_RADIO_CONFIGS.map(config => (
                <PrintRadioGroup
                  key={config.name}
                  title={config.title}
                  name={config.name}
                  currentValue={getPrintOutReceipt0[config.valueKey]}
                  onChange={value =>
                    fn_setTestPrintOutChange(
                      rootStore,
                      config.onChangeParams[0],
                      config.onChangeParams[1],
                      config.onChangeParams[2],
                      value
                    )
                  }
                  disabled={config.checkDisabled ? config.checkDisabled(getPrintOutReceipt0) : false}
                />
              ))}
            </ul>
            <div className="bottBtn flex justify-between">
              <Button
                variant="outline"
                fullWidth={false}
                className="!gap-1 !px-4 !py-3"
                onClick={() => fn_setOriginInfo(rootStore)}
              >
                <img src={settingIcon} alt="testPrint" />
                <span>원산지 설정</span>
              </Button>
              {testPrintOut}
            </div>
          </div>
        </div>
      ) : (
        <div className="box">
          <div className="left">
            {/* 주문서 영수증 미리보기 */}
            <ul className="preview left">
              {RECEIPT_KITCHEN_SECTIONS.map((section, index) => (
                <ReceiptItemRenderer key={`${section.type}-${index}`} item={section} value={getPrintOutReceipt1} />
              ))}
            </ul>
          </div>
          <div className="right">
            <PosNoToggle
              rootStore={rootStore}
              checked={getPrintOutReceipt1.posNo !== "0"}
              onChange={() => handleChangePosView("orderSheet")}
            />
            <ul className="list !mt-4">
              {KITCHEN_PRINT_RADIO_CONFIGS.map(config => (
                <PrintRadioGroup
                  key={config.name}
                  title={config.title}
                  name={config.name}
                  currentValue={getPrintOutReceipt1[config.valueKey]}
                  onChange={value =>
                    fn_setTestPrintOutChange(
                      rootStore,
                      config.onChangeParams[0],
                      config.onChangeParams[1],
                      config.valueKey,
                      value
                    )
                  }
                  disabled={config.checkDisabled ? config.checkDisabled(getPrintOutReceipt1) : false}
                />
              ))}
            </ul>
            <div className="w-full flex justify-end pt-10">{testPrintOut}</div>
          </div>
        </div>
      )}
    </PrintoutLayout>
  );
};

export default inject("rootStore")(observer(SettingOutPrinterOriginSetView));
